import { createContext, useCallback, useContext, useState, ReactNode } from 'react';
import InMeetingConfirmationDialog from '@/components/dialog/InMeetingConfirmationDialog.tsx';

type AskModalQuestionArgs = {
  question: string;
  confirmLabel: string;
  onConfirm: () => void;
};

type ContextType = {
  askModalQuestion: (args: AskModalQuestionArgs) => void;
};

const ConfirmationDialogContext = createContext<ContextType | null>(null);

export const useConfirmationDialog = () => {
  const context = useContext(ConfirmationDialogContext);
  if (!context) {
    throw new Error('useConfirmationDialog must be used within a ConfirmationDialogProvider');
  }
  return context.askModalQuestion;
};

export const ConfirmationDialogProvider = ({ children }: { children: ReactNode }) => {
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const [confirmLabel, setConfirmLabel] = useState('');
  const [onConfirm, setOnConfirm] = useState<() => void>(() => () => {});

  const askModalQuestion = useCallback(({ question, confirmLabel, onConfirm }: AskModalQuestionArgs) => {
    setQuestion(question);
    setConfirmLabel(confirmLabel);
    setOnConfirm(() => () => {
      onConfirm();
      setOpen(false);
    });
    setOpen(true);
  }, []);

  return (
    <ConfirmationDialogContext.Provider value={{ askModalQuestion }}>
      {children}
      <InMeetingConfirmationDialog
        open={open}
        onOpenChange={setOpen}
        question={question}
        confirmLabel={confirmLabel}
        onConfirm={onConfirm}
      />
    </ConfirmationDialogContext.Provider>
  );
};
